import React from 'react';
import { Clock3, User } from 'lucide-react';
import StatusPill from "../../../Components/StatusPill";

export const PatientVisitsCard = ({ patientVisits, isVisitsLoading }) => {
    // Strict capping to maximum 6 items
    const recentVisits = patientVisits?.slice(0, 6) || [];

    // Date aur time formatting helper functions
    const fmt = (d) => d ? new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—";
    const fmtTime = (d) => d ? new Date(d).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "—";

    return (
        <div className=" flex flex-col rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">

            {/* Header Section */}
            <div className="flex items-center justify-between mb-5">
                <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Patient Visits</p>
                    <h2 className="mt-1 text-lg font-bold text-slate-900 dark:text-white">Upcoming & Status</h2>
                </div>
                <div className="inline-flex items-center gap-1.5 rounded-xl bg-slate-50 px-2.5 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-300 border border-slate-100 dark:border-slate-700">
                    <Clock3 className="h-3.5 w-3.5 text-slate-500" /> {patientVisits?.length || 0} Total
                </div>
            </div>

            {/* Main Content Area: Grid System (2 columns on desktop) */}
            <div className="overflow-y-auto max-h-[420px] pr-1 custom-scrollbar">
                <div className="grid gap-4 md:grid-cols-2">

                    {isVisitsLoading ? (
                        // 1. CARDS SKELETON LOADING STATE
                        Array.from({ length: 4 }).map((_, idx) => (
                            <div key={`visit-skeleton-card-${idx}`} className="animate-pulse bg-white rounded-2xl border border-slate-100 p-4 dark:bg-slate-900 dark:border-slate-800">
                                <div className="flex justify-between items-start gap-4">
                                    <div className="space-y-2">
                                        <div className="h-3 w-16 rounded bg-slate-200 dark:bg-slate-800" />
                                        <div className="h-4 w-32 rounded bg-slate-200 dark:bg-slate-800" />
                                    </div>
                                    <div className="h-6 w-16 rounded bg-slate-200 dark:bg-slate-800" />
                                </div>
                                <div className="mt-4 grid grid-cols-2 gap-3">
                                    <div className="h-14 rounded-xl bg-slate-50/60 dark:bg-slate-800/40" />
                                    <div className="h-14 rounded-xl bg-slate-50/60 dark:bg-slate-800/40" />
                                </div>
                            </div>
                        ))
                    ) : recentVisits.length > 0 ? (
                        // 2. REAL VISITS CARDS RENDER
                        recentVisits.map((item, index) => {
                            const patientInfo = item.patient || {};
                            let pillVariant = "info";
                            let pillLabel = "Upcoming";

                            if (item.next_visit_plan) {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const planDate = new Date(item.next_visit_plan);
                                planDate.setHours(0, 0, 0, 0);

                                if (planDate.getTime() < today.getTime()) {
                                    pillVariant = "danger";
                                    pillLabel = "Overdue";
                                } else if (planDate.getTime() === today.getTime()) {
                                    pillVariant = "success";
                                    pillLabel = "Today";
                                } else {
                                    pillVariant = "info";
                                    pillLabel = "Upcoming";
                                }
                            }

                            return (
                                <div
                                    key={item.id || index}
                                    className="relative bg-white rounded-2xl border border-slate-200 shadow-sm p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md dark:bg-slate-900 dark:border-slate-800"
                                >
                                    {/* Top-Right Absolute Fixed Status Pill */}
                                    <div className="absolute top-4 right-4 z-10">
                                        <StatusPill 
                                            value={pillLabel} 
                                            variant={pillVariant} 
                                            flicker={true} 
                                        />
                                    </div>

                                    {/* Top: Patient Identity Details */}
                                    <div className="flex items-start gap-2.5">
                                        <div className="p-2 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-500 shrink-0">
                                            <User className="h-4 w-4" />
                                        </div>
                                        {/* 🔥 FIX 2: pr-[75px] lagaya taaki lamba naam ya ID kabhi bhi badge ke piche na ghuse ya usse takraye nahi */}
                                        <div className="pr-[75px]">
                                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 break-all">
                                                tracking-id: {patientInfo.system_number ? `#${patientInfo.system_number}` : `#${patientInfo.id || '—'}`}
                                            </p>
                                            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mt-0.5 break-words line-clamp-2">
                                                {patientInfo.patient_name || "Unknown"}
                                            </h3>
                                            <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 break-words line-clamp-1">
                                                S/O, W/O: {patientInfo.father_husband_name || "—"}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Bottom: Dates Grid Panels */}
                                    <div className="mt-4 grid grid-cols-2 gap-2.5">
                                        {/* Last Visit Panel */}
                                        <div className="rounded-xl bg-slate-50/70 p-2.5 dark:bg-slate-800/60">
                                            <p className="text-[9px] font-bold uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500">Last Visit</p>
                                            <p className="mt-1 text-xs font-bold text-slate-800 dark:text-slate-200">{fmt(item.visit_date)}</p>
                                            <p className="text-[10px] text-slate-400 mt-0.5">{fmtTime(item.visit_date)}</p>
                                        </div>

                                        {/* Next Planned Panel */}
                                        <div className="rounded-xl bg-slate-50/70 p-2.5 dark:bg-slate-800/60 border border-transparent hover:border-slate-100 dark:hover:border-slate-700">
                                            <p className="text-[9px] font-bold uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500">Next Planned</p>
                                            <p className="mt-1 text-xs font-bold text-slate-800 dark:text-slate-200">{fmt(item.next_visit_plan)}</p>
                                            <p className="text-[10px] text-slate-400 mt-0.5">{fmtTime(item.next_visit_plan)}</p>
                                        </div>
                                    </div>

                                </div>
                            );
                        })
                    ) : (
                        // 3. EMPTY STATE
                        <div className="col-span-full py-10 text-center text-xs font-medium text-slate-400 dark:text-slate-500">
                            No scheduled patient visits found.
                        </div>
                    )}

                </div>
            </div>

        </div>
    );
};